/** Ace artefact review — shown beside the document preview during review or calibration. */

import { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleXmark,
  faRobot,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type {
  AceArtefactRating,
  AceCheckResult,
  AgentRun,
  ArtefactReviewItem,
  CheckAnswer,
  CheckAnswers,
  CheckComments,
  PhaseCriteria,
  Rag,
  ReviewComparison,
} from "./types";
import { ragOption } from "./RagOption";
import { ArtefactApprovalBadge } from "./ArtefactApprovalSelector";
import { AceAgreedWithYouBadge, AceDeferredReason } from "./AceDeferredBadge";
import { CompiledArtefactFixesPanel } from "./CompiledArtefactFixesPanel";
import { aceCheckDeferredToHuman } from "./aceDeferredToHuman";
import { verificationLinkLabel } from "./artefactCompiledFixes";

interface AceArtefactComparisonProps {
  run: AgentRun;
  artefact: ArtefactReviewItem;
  criteria: PhaseCriteria;
  humanAnswers: CheckAnswers;
  humanCheckComments: CheckComments;
  humanRag: Rag | null;
  comparison: ReviewComparison | null;
  loading?: boolean;
  focusCheckId?: string | null;
  readOnly?: boolean;
  onFocusCheck?: (checkId: string) => void;
}

function answerLabel(answer: CheckAnswer): string {
  if (answer === "pass") return "Pass";
  if (answer === "fail") return "Fail";
  return "N/A";
}

function RagBadge({ rag, prefix }: { rag: Rag | null; prefix: string }) {
  if (!rag) {
    return (
      <span className="text-xs text-slate-500">
        {prefix}: <span className="text-slate-400">Not rated</span>
      </span>
    );
  }
  const opt = ragOption(rag);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${opt.text}`}>
      <span className="text-slate-500 font-normal">{prefix}:</span>
      <span className={`w-1.5 h-1.5 rounded-full ${opt.dot}`} aria-hidden />
      {opt.label}
    </span>
  );
}

function aceCheckForId(aceArtefact: AceArtefactRating | undefined, checkId: string): AceCheckResult | undefined {
  return aceArtefact?.checks.find((c) => c.id === checkId);
}

/** Prefer exact artifact_id; fall back to plan_id so Ace still lines up after regenerations. */
function findAceArtefactRating(
  comparison: ReviewComparison | null,
  artefact: ArtefactReviewItem,
): AceArtefactRating | undefined {
  const ratings = comparison?.ace_rating?.artefacts;
  if (!ratings?.length) return undefined;
  const byId = ratings.find((r) => r.artifact_id === artefact.artifact_id);
  if (byId) return byId;
  const planId = artefact.plan_id?.trim();
  if (!planId) return undefined;
  return ratings.find((r) => r.plan_id?.trim() === planId);
}

export function AceArtefactComparison({
  run,
  artefact,
  criteria,
  humanAnswers,
  humanCheckComments,
  humanRag,
  comparison,
  loading = false,
  focusCheckId = null,
  readOnly = false,
  onFocusCheck,
}: AceArtefactComparisonProps) {
  const focusRef = useRef<HTMLDivElement | null>(null);
  const aceArtefact = findAceArtefactRating(comparison, artefact);
  const aceRag = aceArtefact?.suggested_rag ?? null;
  const failedChecks = (aceArtefact?.checks ?? []).filter((check) => check.answer === "fail");
  const deferredCount = (aceArtefact?.checks ?? []).filter((check) => aceCheckDeferredToHuman(check)).length;
  const ragDisagrees = Boolean(humanRag && aceRag && humanRag !== aceRag);

  useEffect(() => {
    if (!focusCheckId || !focusRef.current) return;
    focusRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [focusCheckId, artefact.artifact_id]);

  if (loading && !comparison?.ace_rating) {
    return (
      <div className="rounded-xl bg-white/[0.02] px-4 py-3 ring-1 ring-white/5 space-y-1">
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
          <FontAwesomeIcon icon={faRobot} className="text-indigo-300" />
          Ace review
        </p>
        <p className="text-xs text-slate-500">Ace is rating this LOA…</p>
      </div>
    );
  }

  if (!comparison?.ace_rating) return null;

  if (!aceArtefact) {
    return (
      <div className="rounded-xl bg-white/[0.02] px-4 py-3 ring-1 ring-white/5 space-y-1">
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
          <FontAwesomeIcon icon={faRobot} className="text-indigo-300" />
          Ace review
        </p>
        <p className="text-xs text-slate-500">Ace did not rate this LOA — re-run Ace from Submit.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-indigo-500/[0.04] px-4 py-4 ring-1 ring-indigo-500/15 space-y-4">
      <div className="space-y-2">
        <p className="text-xs font-medium text-indigo-200 uppercase tracking-wide flex items-center gap-1.5">
          <FontAwesomeIcon icon={faRobot} className="text-indigo-300" />
          Ace review
        </p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <RagBadge rag={humanRag} prefix="You" />
          <RagBadge rag={aceRag} prefix="Ace" />
          {ragDisagrees ? (
            <FontAwesomeIcon icon={faCircleXmark} className="text-amber-400 text-xs" />
          ) : humanRag && aceRag ? (
            <FontAwesomeIcon icon={faCircleCheck} className="text-emerald-400 text-xs" />
          ) : null}
        </div>
        <ArtefactApprovalBadge
          decision={aceArtefact.recommended_approval}
          prefix="Ace recommends"
        />
        {deferredCount > 0 ? (
          <p className="text-xs text-slate-400" data-testid="ace-deferred-summary">
            Ace couldn&apos;t verify {deferredCount === 1 ? "1 check" : `${deferredCount} checks`} and
            agreed with your rating
            {deferredCount === 1 ? "" : "s"}.
          </p>
        ) : null}
      </div>

      {failedChecks.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
            Suggested fixes
          </p>
          {failedChecks.map((aceCheck) => {
            const check = criteria.checks.find((item) => item.id === aceCheck.id);
            const deferred = aceCheckDeferredToHuman(aceCheck);
            return (
              <div
                key={aceCheck.id}
                className="rounded-lg bg-amber-500/[0.06] px-3 py-2.5 text-xs ring-1 ring-amber-500/20 space-y-1"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-slate-200">{check?.label ?? aceCheck.id}</p>
                  {deferred ? <AceAgreedWithYouBadge compact /> : null}
                </div>
                {aceCheck.reason ? (
                  deferred ? (
                    <AceDeferredReason reason={aceCheck.reason} />
                  ) : (
                    <p className="text-slate-500 leading-relaxed">{aceCheck.reason}</p>
                  )
                ) : null}
                {aceCheck.recommendation ? (
                  <div className="space-y-1">
                    <p className="text-slate-400 leading-relaxed">Fix: {aceCheck.recommendation.text}</p>
                    {aceCheck.recommendation?.action_url ? (
                      <a
                        href={aceCheck.recommendation.action_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-indigo-300 hover:text-indigo-200"
                      >
                        {verificationLinkLabel(aceCheck.recommendation)}
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <CompiledArtefactFixesPanel
        run={run}
        artefact={artefact}
        criteria={criteria}
        aceArtefact={aceArtefact}
      />

      <div className="space-y-2">
        <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Check-by-check</p>
        {criteria.checks.map((check) => {
          const humanAnswer = humanAnswers[check.id] ?? null;
          const humanComment = humanCheckComments[check.id]?.trim() ?? "";
          const aceCheck = aceCheckForId(aceArtefact, check.id);
          const deferred = aceCheckDeferredToHuman(aceCheck);
          const disagrees = Boolean(
            humanAnswer && aceCheck && humanAnswer !== aceCheck.answer,
          );
          const focused = check.id === focusCheckId;
          return (
            <div
              key={check.id}
              ref={focused ? focusRef : undefined}
              className={`rounded-lg px-3 py-2.5 text-xs space-y-1 ${
                focused
                  ? "bg-indigo-500/[0.08] ring-2 ring-indigo-500/40"
                  : disagrees
                    ? "bg-amber-500/[0.06] ring-1 ring-amber-500/25"
                    : deferred
                      ? "bg-slate-500/[0.06] ring-1 ring-dashed ring-slate-400/25"
                      : "bg-white/[0.02] ring-1 ring-white/5"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-slate-200 font-medium">{check.label}</p>
                {deferred ? <AceAgreedWithYouBadge compact /> : null}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-400">
                <span>
                  {readOnly ? "Human" : "You"}:{" "}
                  <span className="text-slate-200 font-medium">
                    {humanAnswer ? answerLabel(humanAnswer) : "—"}
                  </span>
                </span>
                <span>
                  Ace:{" "}
                  <span className="text-slate-200 font-medium">
                    {aceCheck ? answerLabel(aceCheck.answer) : "—"}
                  </span>
                  {deferred ? (
                    <span className="text-slate-500 font-normal"> (mirrored)</span>
                  ) : null}
                </span>
              </div>
              {humanComment ? (
                <p className="text-slate-500 leading-relaxed">
                  {readOnly ? "Human" : "You"}: {humanComment}
                </p>
              ) : null}
              {aceCheck?.reason ? (
                deferred ? (
                  <AceDeferredReason reason={aceCheck.reason} />
                ) : (
                  <p className="text-slate-500 leading-relaxed">Ace: {aceCheck.reason}</p>
                )
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
